import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import { ApiError } from "@/api/client";
import { AuthSplitLayout } from "@/components/auth/auth-split-layout";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Alert } from "@/components/ui/alert";
import { ROUTES } from "@/lib/constants";

interface LocationState {
  from?: { pathname: string };
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const redirectTo =
    (location.state as LocationState | null)?.from?.pathname ??
    ROUTES.DASHBOARD;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // Backend returns 401 for invalid creds, 422 for rate-limit
        // / lockout, etc. We surface a user-friendly message but
        // keep the technical code out of the UI.
        if (err.status === 401) {
          setError(t("auth.login.errors.invalidCredentials"));
        } else if (err.status === 429) {
          setError(t("auth.login.errors.rateLimited"));
        } else {
          setError(err.message);
        }
      } else {
        setError(
          err instanceof Error
            ? err.message
            : t("auth.login.errors.unexpected")
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSplitLayout>
      <div className="space-y-8">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold text-ocean-900 tablet:text-4xl">
            {t("auth.login.title")}
          </h1>
          <p className="text-sm text-neutral-600 tablet:text-base">
            {t("auth.login.subtitle")}
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-5">
          <FormField
            label={t("auth.login.emailLabel")}
            type="email"
            value={email}
            onChange={setEmail}
            placeholder={t("auth.login.emailPlaceholder")}
            autoComplete="email"
            required
          />
          <FormField
            label={t("auth.login.passwordLabel")}
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />

          {error && <Alert variant="danger">{error}</Alert>}

          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading
              ? t("auth.login.submitLoading")
              : t("auth.login.submitButton")}
          </Button>
        </form>

        <p className="text-sm text-neutral-600">
          {t("auth.login.noAccount")}{" "}
          <Link
            to={ROUTES.REGISTER}
            className="font-medium text-ocean-600 underline-offset-4 hover:text-ocean-800 hover:underline"
          >
            {t("auth.login.registerLink")}
          </Link>
        </p>
      </div>
    </AuthSplitLayout>
  );
}
